import React from "react";
import Button from "./Button";

export interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
}

interface Props<T extends { id: string }> {
  columns: Column<T>[];
  data: T[];
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  onView?: (row: T) => void;
  extraActions?: (row: T) => React.ReactNode;
  emptyMessage?: string;
}

function Table<T extends { id: string }>({
  columns, data, onEdit, onDelete, onView, extraActions, emptyMessage = "No records found",
}: Props<T>) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/30"
      style={{ background: "rgba(255,255,255,0.35)" }}>
      <table className="w-full text-sm min-w-max">
        <thead>
          <tr className="border-b border-gray-200/60"
            style={{ background: "rgba(255,255,255,0.65)" }}>
            {columns.map(col => (
              <th key={String(col.key)}
                className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {col.label}
              </th>
            ))}
            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="text-center py-14 text-gray-400">
                <div className="text-4xl mb-2">📭</div>
                <p>{emptyMessage}</p>
              </td>
            </tr>
          )}
          {data.map((row, i) => (
            <tr key={row.id || i}
              className="border-b border-gray-100/60 hover:bg-white/50 transition-colors">
              {columns.map(col => (
                <td key={String(col.key)} className="px-4 py-3 text-gray-700">
                  {col.render
                    ? col.render((row as any)[col.key], row)
                    : String((row as any)[col.key] ?? "—")}
                </td>
              ))}
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1.5">
                  {extraActions?.(row)}
                  {onView   && <Button size="sm" variant="ghost" onClick={() => onView(row)}>👁</Button>}
                  {onEdit   && <Button size="sm" variant="ghost" onClick={() => onEdit(row)}>✏️</Button>}
                  {onDelete && <Button size="sm" variant="ghost" onClick={() => onDelete(row)}>🗑</Button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
